import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Mail, Building2, Save, TestTube } from "lucide-react";
import { toast } from "sonner";

export default function SettingsPage() {
  const { apiFetch, API } = useAuth();
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: "smtp.hostinger.com", smtp_port: 465, smtp_user: "contact@soudurejvc.com", smtp_password: "",
    from_name: "Soudure JVC", from_email: "contact@soudurejvc.com",
    imap_host: "imap.hostinger.com", imap_port: 993
  });
  const [companySettings, setCompanySettings] = useState({
    company_name: "SAS J.V.C", legal_form: "SAS", siren: "984 545 004", siret: "984 545 004 00017",
    ape_code: "33.20A", activities: "Structure métallique, Soudure industrielle, Serrurerie",
    address: "15, Rue Martin Luther King", city: "KOUROU", postal_code: "97310",
    phone: "0594469823", mobile: "+33 781564422", email: "Contact.jvc973@gmail.com",
    iban: "FR76 2673 3000 1013 0191 3106 320", bic: "SOXAFR2L"
  });
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      const [emailRes, companyRes] = await Promise.all([
        apiFetch(`${API}/settings/email`),
        apiFetch(`${API}/settings/company`)
      ]);
      if (emailRes.ok) setEmailSettings(await emailRes.json());
      if (companyRes.ok) setCompanySettings(await companyRes.json());
    };
    fetchSettings();
  }, [apiFetch, API]);

  const saveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      const res = await apiFetch(`${API}/settings/email`, { method: "PUT", body: JSON.stringify(emailSettings) });
      if (res.ok) toast.success("Paramètres email sauvegardés");
      else toast.error("Erreur lors de la sauvegarde");
    } finally { setSavingEmail(false); }
  };

  const testEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await apiFetch(`${API}/settings/email/test`, { method: "POST" });
      if (res.ok) toast.success("Email de test envoyé !");
      else { const err = await res.json(); toast.error(err.detail || "Erreur lors de l'envoi du test"); }
    } finally { setTestingEmail(false); }
  };

  const saveCompanySettings = async () => {
    setSavingCompany(true);
    try {
      const res = await apiFetch(`${API}/settings/company`, { method: "PUT", body: JSON.stringify(companySettings) });
      if (res.ok) toast.success("Informations entreprise sauvegardées");
      else toast.error("Erreur lors de la sauvegarde");
    } finally { setSavingCompany(false); }
  };

  const F = (field, value) => setEmailSettings(s => ({...s, [field]: value}));
  const C = (field, value) => setCompanySettings(s => ({...s, [field]: value}));

  return (
    <div className="p-8 max-w-3xl" data-testid="settings-page">
      <div className="mb-6">
        <h1 className="font-barlow text-2xl font-bold text-slate-900 tracking-tight uppercase">Paramètres</h1>
        <p className="text-sm text-slate-500 mt-1">Configuration de l'application</p>
      </div>

      <Tabs defaultValue="email">
        <TabsList className="mb-6">
          <TabsTrigger value="email" data-testid="tab-email"><Mail className="w-4 h-4 mr-2" /> Configuration Email</TabsTrigger>
          <TabsTrigger value="company" data-testid="tab-company"><Building2 className="w-4 h-4 mr-2" /> Informations Entreprise</TabsTrigger>
        </TabsList>

        <TabsContent value="email">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base">Configuration Email (Hostinger)</CardTitle>
              <CardDescription>
                Paramètres SMTP et IMAP pour l'envoi et la réception d'emails via contact@soudurejvc.com
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Serveur sortant (SMTP)</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Serveur SMTP</Label>
                  <Input value={emailSettings.smtp_host} onChange={e => F("smtp_host", e.target.value)} data-testid="smtp-host-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Port SMTP</Label>
                  <Input type="number" value={emailSettings.smtp_port} onChange={e => F("smtp_port", parseInt(e.target.value))} data-testid="smtp-port-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Email</Label>
                  <Input type="email" value={emailSettings.smtp_user} onChange={e => F("smtp_user", e.target.value)} data-testid="smtp-user-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Mot de passe</Label>
                  <Input type="password" value={emailSettings.smtp_password} onChange={e => F("smtp_password", e.target.value)} placeholder="••••••••" data-testid="smtp-password-input" />
                </div>
              </div>
              <Separator />
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Serveur entrant (IMAP)</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Serveur IMAP</Label>
                  <Input value={emailSettings.imap_host || ""} onChange={e => F("imap_host", e.target.value)} data-testid="imap-host-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Port IMAP</Label>
                  <Input type="number" value={emailSettings.imap_port || 993} onChange={e => F("imap_port", parseInt(e.target.value))} data-testid="imap-port-input" />
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Nom d'expéditeur</Label>
                  <Input value={emailSettings.from_name} onChange={e => F("from_name", e.target.value)} data-testid="from-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Email d'expéditeur</Label>
                  <Input type="email" value={emailSettings.from_email} onChange={e => F("from_email", e.target.value)} data-testid="from-email-input" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button onClick={saveEmailSettings} disabled={savingEmail} className="bg-slate-900 hover:bg-slate-800" data-testid="save-email-btn">
                  <Save className="w-4 h-4 mr-2" /> {savingEmail ? "Sauvegarde..." : "Sauvegarder"}
                </Button>
                <Button onClick={testEmail} disabled={testingEmail} variant="outline" data-testid="test-email-btn">
                  <TestTube className="w-4 h-4 mr-2" /> {testingEmail ? "Envoi..." : "Envoyer un test"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="company">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-base">Informations de l'entreprise</CardTitle>
              <CardDescription>Ces informations apparaissent sur vos devis, factures et bons de commande</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Nom de l'entreprise</Label>
                  <Input value={companySettings.company_name} onChange={e => C("company_name", e.target.value)} data-testid="company-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Forme juridique</Label>
                  <Input value={companySettings.legal_form} onChange={e => C("legal_form", e.target.value)} data-testid="legal-form-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-700">Activités</Label>
                <Input value={companySettings.activities} onChange={e => C("activities", e.target.value)} data-testid="company-activities-input" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-700">SIREN</Label>
                  <Input value={companySettings.siren} onChange={e => C("siren", e.target.value)} data-testid="company-siren-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">SIRET</Label>
                  <Input value={companySettings.siret} onChange={e => C("siret", e.target.value)} data-testid="company-siret-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Code APE</Label>
                  <Input value={companySettings.ape_code} onChange={e => C("ape_code", e.target.value)} data-testid="company-ape-input" />
                </div>
              </div>
              <Separator />
              <div className="space-y-1.5">
                <Label className="text-slate-700">Adresse</Label>
                <Input value={companySettings.address} onChange={e => C("address", e.target.value)} data-testid="company-address-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Code postal</Label>
                  <Input value={companySettings.postal_code} onChange={e => C("postal_code", e.target.value)} data-testid="company-postal-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Ville</Label>
                  <Input value={companySettings.city} onChange={e => C("city", e.target.value)} data-testid="company-city-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Téléphone</Label>
                  <Input value={companySettings.phone} onChange={e => C("phone", e.target.value)} data-testid="company-phone-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Mobile</Label>
                  <Input value={companySettings.mobile} onChange={e => C("mobile", e.target.value)} data-testid="company-mobile-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">Email</Label>
                  <Input type="email" value={companySettings.email} onChange={e => C("email", e.target.value)} data-testid="company-email-input" />
                </div>
              </div>
              <Separator />
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Coordonnées bancaires</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-slate-700">IBAN</Label>
                  <Input value={companySettings.iban} onChange={e => C("iban", e.target.value)} data-testid="company-iban-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-slate-700">BIC</Label>
                  <Input value={companySettings.bic} onChange={e => C("bic", e.target.value)} data-testid="company-bic-input" />
                </div>
              </div>
              <Button onClick={saveCompanySettings} disabled={savingCompany} className="bg-slate-900 hover:bg-slate-800" data-testid="save-company-btn">
                <Save className="w-4 h-4 mr-2" /> {savingCompany ? "Sauvegarde..." : "Sauvegarder"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <p className="text-[9px] text-slate-300 text-center mt-8">CopyRight by De La Rosa Design</p>
    </div>
  );
}
